import { cn } from '@sglara/cn';
import { useQuery } from '@tanstack/react-query';
import { Modal, ModalTitle, MODAL_OUTER_PADDING } from 'design-system/src/components/Modal';
import { ModalGeneralInfoTable } from 'design-system/src/components/GeneralInfoTable/ModalGeneralInfoTable/ModalGeneralInfoTable';
import { IframeWithToolbar } from 'design-system/src/components/IframeWithToolbar/IframeWithToolbar';
import { LoadingSpinner } from 'design-system/src/components/LoadingSpinner/LoadingSpinner';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { CustomPaForm } from '../priorAuthReviews/CustomPaForm/CustomPaForm';
import { client, getRxFilePreviewUrl, getRxFileDownloadUrl } from '../../api';
import type { PriorAuthReviewFormQuestionAnswer, RxResponse } from '../../api';

export type SubmittedPAModalProps = {
  /**
   * Class name for additional styling
   */
  className?: string;
  /**
   * The prescription ID
   */
  rxId: string;
  /**
   * The form tree state ID, if this submission has a form tree
   */
  formTreeStateId: string | null;
  /**
   * The merged document ID, if this submission has a PDF fallback
   */
  submissionMergedDocumentId: string | null;
  /**
   * Called when the modal is closed
   */
  onClose: () => void;
};

type PASubmissionFormData = {
  form: {
    ordered_question_answers: PriorAuthReviewFormQuestionAnswer[];
  };
  form_status: string;
};

/**
 * Modal for reviewing a submitted PA. Shows the form tree Q&A if available,
 * otherwise falls back to displaying the submitted PDF document.
 */
export const SubmittedPAModal = ({
  className,
  rxId,
  formTreeStateId,
  submissionMergedDocumentId,
  onClose,
}: SubmittedPAModalProps) => {
  const rxQuery = useQuery<RxResponse>({
    queryKey: ['rxs', rxId],
    queryFn: async () => {
      const res = await client.get(`rxs/${rxId}`);
      return res.data;
    },
  });

  const formQuery = useQuery<PASubmissionFormData>({
    queryKey: ['paSubmissionForm', rxId, formTreeStateId],
    enabled: !!formTreeStateId,
    queryFn: async () => {
      const res = await client.get(`/rxs/${rxId}/pa-submission/${formTreeStateId}/form`);
      return res.data;
    },
  });

  const patient = rxQuery.data?.patient;
  const prescriber = rxQuery.data?.prescriber;
  const drug = rxQuery.data?.drug;
  const modalContentPadding = `${MODAL_OUTER_PADDING} ${MODAL_OUTER_PADDING} 0`;

  const renderContent = () => {
    if (formQuery.isLoading) {
      return <LoadingSpinner height="200px" />;
    }

    if (formTreeStateId && formQuery.data) {
      return (
        <CustomPaForm
          form={formQuery.data.form}
          formDisabled
          formId={`pa-submission-form-${formTreeStateId}`}
          saveQuestionAnswer={() => {}}
          attestationCheckboxIsChecked={false}
          setAttestationCheckboxIsChecked={() => {}}
          showAttestationCheckboxError={false}
          setShowAttestationCheckboxError={() => {}}
          attestationCheckboxErrorMessageId=""
          attestationSubmitted={false}
        />
      );
    }

    if (submissionMergedDocumentId) {
      return (
        <div style={{ padding: modalContentPadding }}>
          <IframeWithToolbar
            fileUrl={getRxFilePreviewUrl(rxId, submissionMergedDocumentId)}
            downloadUrl={getRxFileDownloadUrl(rxId, submissionMergedDocumentId)}
            iframeTitle="Submitted PA document"
          />
        </div>
      );
    }

    return (
      <div className="flex items-center justify-center p-8">
        <Typography color="var(--grayscale-600)">No submission data available</Typography>
      </div>
    );
  };

  return (
    <Modal onClose={onClose} fullHeight wide>
      <ModalTitle>Review submitted PA</ModalTitle>
      {/* using style because variables don't work in tailwind padding classnames */}
      <div style={{ padding: modalContentPadding }}>
        {!rxQuery.isLoading && (
          <ModalGeneralInfoTable
            patientName={patient ? `${patient.first_name} ${patient.last_name}` : null}
            prescriberName={prescriber?.name || null}
            dob={patient?.date_of_birth || null}
            prescribedOn={rxQuery.data?.prescription_recieved_at || null}
            drugName={drug?.name || null}
            prescriptionId={drug?.id || null}
          />
        )}
      </div>
      <div
        id={formTreeStateId ? `pa-submission-form-${formTreeStateId}` : undefined}
        className={cn('overflow-y-auto', className)}
      >
        {renderContent()}
      </div>
    </Modal>
  );
};

export default SubmittedPAModal;
